import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaCalendarAlt,
    FaChartPie,
    FaCheckCircle,
    FaCoins,
    FaCrown,
    FaEthereum,
    FaFire,
    FaGem,
    FaGlobe,
    FaHandHoldingUsd,
    FaRocket,
    FaShieldAlt,
    FaUsers
} from "react-icons/fa";
import { TiArrowUp } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const ICO = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }

    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  // Token Stats
  const tokenStats = [
    { value: "50", label: "ERC-20 Tokens", icon: <FaCoins /> },
    { value: "1B", label: "Total Supply", icon: <FaChartPie /> },
    { value: "ETH", label: "Blockchain", icon: <FaEthereum /> },
    { value: "Q2 2026", label: "Launch Date", icon: <FaCalendarAlt /> },
  ];

  // Token Sale Phases
  const salePhases = [
    {
      phase: "Private Sale",
      status: "Completed",
      price: "£0.008",
      discount: "60%",
      allocation: "150M",
      raised: "£1.2M",
      icon: <FaCrown />,
      color: "purple",
    },
    {
      phase: "Pre-Sale",
      status: "Active",
      price: "£0.012",
      discount: "40%",
      allocation: "200M",
      raised: "£800K",
      icon: <FaFire />,
      color: "orange",
    },
    {
      phase: "Public Sale",
      status: "Upcoming",
      price: "£0.018",
      discount: "10%",
      allocation: "250M",
      raised: "—",
      icon: <FaGlobe />,
      color: "cyan",
    },
    {
      phase: "Launch",
      status: "Q2 2026",
      price: "£0.020",
      discount: "0%",
      allocation: "400M",
      raised: "—",
      icon: <FaRocket />,
      color: "emerald",
    },
  ];

  // Token Allocation (for visual pie representation)
  const tokenAllocation = [
    { name: "Public Sale", percentage: 40, color: "#22d3ee" },
    { name: "Ecosystem & Rewards", percentage: 20, color: "#a855f7" },
    { name: "Team & Advisors", percentage: 15, color: "#f97316" },
    { name: "Development", percentage: 10, color: "#10b981" },
    { name: "Marketing", percentage: 8, color: "#ec4899" },
    { name: "Reserve", percentage: 7, color: "#6366f1" },
  ];

  // Token Utility
  const tokenUtilities = [
    {
      title: "Governance",
      description: "Vote on protocol upgrades, feature proposals, and ecosystem decisions",
      icon: <FaUsers />,
    },
    {
      title: "Staking Rewards",
      description: "Stake tokens to earn up to 15% APY through PoS validation and unlock premium features",
      icon: <FaGem />,
    },
    {
      title: "Transaction Fees",
      description: "Pay reduced fees across all Zero-Blockchain products and services",
      icon: <FaHandHoldingUsd />,
    },
    {
      title: "Access & Benefits",
      description: "Unlock exclusive features, early access to new products, and VIP support",
      icon: <FaCrown />,
    },
  ];

  // Roadmap
  const roadmap = [
    { quarter: "Q4 2025", milestone: "Private Sale & Smart Contract Audit", status: "completed" },
    { quarter: "Q1 2026", milestone: "Pre-Sale Launch & Exchange Listings", status: "active" },
    { quarter: "Q2 2026", milestone: "Public Sale & Token Launch", status: "upcoming" },
    { quarter: "Q3 2026", milestone: "DEX Integration & Staking Platform", status: "upcoming" },
    { quarter: "Q4 2026", milestone: "Cross-Chain Bridge & DeFi Expansion", status: "upcoming" },
  ];

  // Color schemes
  const colorSchemes = {
    cyan: { bg: "bg-cyan-500", text: "text-cyan-400", border: "border-cyan-500/50" },
    purple: { bg: "bg-purple-500", text: "text-purple-400", border: "border-purple-500/50" },
    orange: { bg: "bg-orange-500", text: "text-orange-400", border: "border-orange-500/50" },
    emerald: { bg: "bg-emerald-500", text: "text-emerald-400", border: "border-emerald-500/50" },
    pink: { bg: "bg-pink-500", text: "text-pink-400", border: "border-pink-500/50" },
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/ico-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="icoHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Zero Token ICO"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Early Access · Governance Rights · Future Economy
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          additionalClasses=""
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        
        {/* Introduction */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Invest in the Future of Finance
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            The Zero Token ICO introduces an innovative funding mechanism for our blockchain ecosystem. 
            Join the revolution in decentralised finance and conservation technology.
          </p>
        </motion.div>

        {/* Token Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {tokenStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl text-center group-hover:border-cyan-500/30 transition-all duration-500">
                  <div className="text-cyan-400 text-2xl mb-3 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Sale Phases Section */}
        <div className="mb-20" ref={(el) => (sectionRefs.current[0] = el)}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Token Sale Phases
            </span>
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-300 text-center mb-12 max-w-xl mx-auto"
          >
            Join early for the best prices and exclusive bonuses
          </motion.p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {salePhases.map((phase, index) => {
              const colors = colorSchemes[phase.color];
              const isActive = phase.status === "Active";
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={`group relative ${isActive ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-black' : ''}`}
                >
                  {isActive && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full z-10">
                      LIVE NOW
                    </div>
                  )}
                  <div className={`relative h-full bg-black/80 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:${colors.border} transition-all duration-500 flex flex-col`}>
                    {/* Phase Icon */}
                    <div className={`w-12 h-12 rounded-xl ${colors.bg}/20 flex items-center justify-center text-xl ${colors.text} mb-4`}>
                      {phase.icon}
                    </div>
                    
                    {/* Phase Name & Status */}
                    <h4 className="text-xl font-bold text-white mb-1">{phase.phase}</h4>
                    <span className={`text-xs ${colors.text} mb-4`}>{phase.status}</span>
                    
                    {/* Details */}
                    <div className="space-y-3 flex-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Price:</span>
                        <span className="text-white font-medium">{phase.price}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Discount:</span>
                        <span className={`${colors.text} font-medium`}>{phase.discount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Allocation:</span>
                        <span className="text-white font-medium">{phase.allocation}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">Raised:</span>
                        <span className="text-white font-medium">{phase.raised}</span>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    {isActive && (
                      <GlassButton
                        className="mt-4 w-full"
                        variant={phase.color}
                        onClick={() => {}} // Placeholder for buy action
                        size="md"
                      >
                        Buy Now
                      </GlassButton>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Token Allocation Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
          <div className="absolute inset-0 bg-black/80" />
          
          <div className="relative z-10 py-16 px-8">
            <h3 className="text-3xl font-bold text-center mb-12 text-white">
              Token Allocation
            </h3>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Visual Pie Chart Representation */}
              <div className="flex justify-center">
                <div className="relative w-64 h-64">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 blur-2xl" />
                  <div className="relative w-full h-full rounded-full border-4 border-white/10 flex items-center justify-center overflow-hidden">
                    {/* Gradient segments simulation */}
                    <div className="absolute inset-2 rounded-full" style={{
                      background: `conic-gradient(
                        #22d3ee 0deg 144deg,
                        #a855f7 144deg 216deg,
                        #f97316 216deg 270deg,
                        #10b981 270deg 306deg,
                        #ec4899 306deg 334.8deg,
                        #6366f1 334.8deg 360deg
                      )`
                    }} />
                    <div className="absolute inset-8 rounded-full bg-black flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white">1B</div>
                        <div className="text-xs text-gray-300">Total Supply</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Allocation Legend */}
              <div className="space-y-4">
                {tokenAllocation.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-white">{item.name}</span>
                    </div>
                    <span className="text-lg font-bold" style={{ color: item.color }}>{item.percentage}%</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Token Utility Section */}
        <div className="mb-20" ref={(el) => (sectionRefs.current[2] = el)}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12"
          >
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Token Utility
            </span>
          </motion.h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tokenUtilities.map((utility, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group text-center p-6 bg-black/50 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-cyan-500/30 transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-2xl text-cyan-400 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  {utility.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{utility.title}</h4>
                <p className="text-gray-300 text-sm">{utility.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Roadmap Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <h3 className="text-3xl font-bold text-center mb-12">
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              ICO Roadmap
            </span>
          </h3>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-500 via-purple-500 to-pink-500 hidden md:block" />
            
            <div className="space-y-8">
              {roadmap.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`inline-block p-4 rounded-2xl bg-black/50 backdrop-blur-sm border ${
                      item.status === 'completed' ? 'border-emerald-500/50' :
                      item.status === 'active' ? 'border-orange-500/50' :
                      'border-white/10'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {item.status === 'completed' && <FaCheckCircle className="text-emerald-400" />}
                        {item.status === 'active' && <FaFire className="text-orange-400" />}
                        <span className={`text-sm font-bold ${
                          item.status === 'completed' ? 'text-emerald-400' :
                          item.status === 'active' ? 'text-orange-400' :
                          'text-gray-300'
                        }`}>
                          {item.quarter}
                        </span>
                      </div>
                      <p className="text-white">{item.milestone}</p>
                    </div>
                  </div>
                  
                  {/* Center dot */}
                  <div className={`hidden md:flex w-4 h-4 rounded-full ${
                    item.status === 'completed' ? 'bg-emerald-500' :
                    item.status === 'active' ? 'bg-orange-500 animate-pulse' :
                    'bg-gray-600'
                  } border-4 border-black z-10`} />
                  
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Security Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-blue-500/10" />
          <div className="absolute inset-0 bg-black/80" />
          
          <div className="relative z-10 py-12 px-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                  <FaShieldAlt className="text-5xl text-emerald-400" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Smart Contract Security</h3>
                <p className="text-gray-300 mb-4">
                  Our token smart contracts are built on Ethereum's robust infrastructure and have undergone 
                  comprehensive security audits. All transactions are transparent, immutable, and protected 
                  by industry-leading encryption standards.
                </p>
                <div className="flex flex-wrap gap-4">
                  <span className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm">
                    ✓ Audited by CertiK
                  </span>
                  <span className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm">
                    ✓ Multi-Sig Treasury
                  </span>
                  <span className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm">
                    ✓ Locked Liquidity
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[5] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          <div className="relative z-10 py-16 px-8 text-center">
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-400 text-sm mb-6"
            >
              <FaFire /> Pre-Sale Now Live
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Don't Miss the Opportunity
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              Join the Zero Token ICO today and be part of the blockchain revolution. 
              Early investors receive up to 40% discount on token purchases.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GlassButton
                className="px-8 py-4"
                variant="pink"
                size="lg"
                onClick={() => {}} // Placeholder
              >
                <div className="flex items-center gap-2">
                  <FaRocket /> Buy Tokens Now
                </div>
              </GlassButton>
              <GlassButton
                onClick={() => navigate('/tokenomics')}
                className="px-8 py-4"
                variant="gray"
                size="lg"
              >
                <div className="flex items-center gap-2">
                  <FaChartPie /> View Tokenomics
                </div>
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ICO;
